import { GoogleSupportedLanguage } from 'dashboard/lib/constants/locales';
import {
  AugmentationMethod,
  ComparisonMethod,
  NormalizationMethod,
} from 'dashboard/models/verification-check';
import { FieldsMap } from 'lib/fields';
import { ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';

// todo: reorganize this file to better group related types together

export enum DefinitionType {
  Preset = 'preset',
  Custom = 'custom',
}

export enum GuidedPreset {
  GenericLooseAi = 'generic_loose_ai',
  GenericStrict = 'generic_strict',
  GenericLoose = 'generic_loose',
  AddressStrict = 'address_strict',
  AddressLoose = 'address_loose',
  NameStrict = 'name_strict',
  NameLoose = 'name_loose',
}

export enum ClassificationMethod {
  Ai = 'ai',
  Rules = 'rules',
}

export enum ToggleOption {
  Enabled = 'enabled',
  Disabled = 'disabled',
}

export interface Classification {
  key: string;
  'definition-type': DefinitionType;
  'classification-method': ClassificationMethod;
  label: string;
  description: string | null;
  'toggle-option': ToggleOption;
}

export enum DocumentStatus {
  Initiated = 'initiated',
  Submitted = 'submitted',
  Processed = 'processed',
  Errored = 'errored',
}

export interface DocumentFile {
  'byte-size': number;
  'file-type': string;
  filename: string;
  url: string;
}

export interface DocumentCoreAttributes {
  filenames: string[];
  kind: string;
  'document-type'?: string | null;
  status: DocumentStatus;
  'created-at': string;
  'submitted-at': string | null;
  'processed-at': string | null;
  'redacted-at': string | null;
}

export enum ExtractionFieldType {
  Date = 'date',
  PhoneNumber = 'phone_number',
  Address = 'address',
  String = 'string',
  NameFull = 'name_full',

  // 'AI' is only defined on the front-end configuration experience and maps to 'string' + an AI model configuration on the back-end
  Ai = 'ai',
}

export const EXTRACTION_FIELD_TYPE_TO_DISPLAY_NAME: Record<ExtractionFieldType, string> = {
  [ExtractionFieldType.Date]: 'Date',
  [ExtractionFieldType.PhoneNumber]: 'Phone number',
  [ExtractionFieldType.Address]: 'Address',
  [ExtractionFieldType.String]: 'String',
  [ExtractionFieldType.NameFull]: 'Name full',
  [ExtractionFieldType.Ai]: 'AI',
};

export const EXTRACTION_FIELD_TYPE_NAME_FULL_FIELDS = ['name_first', 'name_middle', 'name_last'];

export const EXTRACTION_FIELD_TYPE_ADDRESS_FIELDS = [
  'address_street_1',
  'address_street_2',
  'address_city',
  'address_subdivision',
  'address_postal_code',
];

export enum ExtractionType {
  Guided = 'guided',
  Unguided = 'unguided',
}

export enum MatchLevel {
  Full = 'full',
  Partial = 'partial',
  None = 'none',
}

export enum ExtractorType {
  Ai = 'ai',
  Text = 'text',
  Regex = 'regex',
  KeyValuePair = 'key_value_pair',
  // deprecated
  Preset = 'preset',
  Paystub = 'paystub',
  Tables = 'tables',
  BankStatement = 'bank_statement',
  // dashboard only: represents guided extraction
  InquiryFieldComparison = 'inquiry_field_comparison',
  TransactionFieldComparison = 'transaction_field_comparison',
}

export enum ExtractAction {
  Accept = 'accept',
  Reject = 'reject',
}

export interface BoundingBox {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface ExtractionResultInfo {
  'field-name': string;
  value: string;
  id: number;
}
export interface ExtractionResponseResult {
  value: string;
  page: number;
  'edit-distance': number;
  'bounding-box': BoundingBox;
  metadata: Record<string, unknown>;
  'match-level': MatchLevel;
  // unique id generated on the client side to differentiate ExtractionResponseResults  within the same extraction
  // used on Document Viewer
  id?: number;
}

export interface ExtractionResponse {
  'extraction-type': ExtractionType;
  'field-name': string;
  'field-type': ExtractionFieldType;
  'source-value'?: string;
  'match-level-minimum'?: MatchLevel;
  results: ExtractionResponseResult[];
}

export interface TextExtractor {
  'extract-pattern': string;
  'extract-action': ExtractAction;
  'edit-distance-maximum': number;
}

export interface UnguidedTextExtractionRequest {
  version: '2.0';
  'extraction-type': ExtractionType.Unguided;
  'field-type': ExtractionFieldType;
  'field-name': string;
  'extractor-type': ExtractorType.Text;
  'text-extractors': TextExtractor[];
  // deprecated fields
  name?: string;
  methods?: [];
}

export interface RegexExtractor {
  'extract-pattern': string;
  'extract-action': ExtractAction;
  'extract-name'?: string;
}

export interface UnguidedRegexExtractionRequest {
  version: '2.0';
  'extraction-type': ExtractionType.Unguided;
  'field-type': ExtractionFieldType;
  'field-name': string;
  'extractor-type': ExtractorType.Regex;
  'regex-extractors': RegexExtractor[];
  // deprecated fields
  name?: string;
  methods?: [];
}

export enum PresetExtractor {
  Address = 'address',
  ChineseChop = 'chinese_chop',
  Date = 'date',
  PhoneNumber = 'phone_number',
  Price = 'price',
  Person = 'person',
  PersonEnglish = 'person_english',
  Organization = 'organization',
  OrganizationEnglish = 'organization_english',
  Ssn = 'social_security_number',
  Ein = 'employer_identification_number',
  Vin = 'vehicle_identification_number',
  LicensePlateJpClass = 'license_plate_jp_class',
  LicensePlateJpText = 'license_plate_jp_text',
}

export const DEPRECATED_PRESET_EXTRACTORS: PresetExtractor[] = [
  PresetExtractor.Address,
  PresetExtractor.Date,
  PresetExtractor.PhoneNumber,
  PresetExtractor.Price,
  PresetExtractor.Person,
  PresetExtractor.Organization,
  PresetExtractor.OrganizationEnglish,
  PresetExtractor.Ein,
  PresetExtractor.Vin,
  PresetExtractor.LicensePlateJpClass,
  PresetExtractor.LicensePlateJpText,
];

export const PRESET_EXTRACTOR_TO_DISPLAY_NAME: Record<PresetExtractor, string> = {
  [PresetExtractor.PersonEnglish]: 'English Name',
  [PresetExtractor.ChineseChop]: 'Chinese Chop',
  [PresetExtractor.Ssn]: 'US Social Security Number',
  [PresetExtractor.Address]: 'Address',
  [PresetExtractor.Date]: 'Date',
  [PresetExtractor.PhoneNumber]: 'Phone Number',
  [PresetExtractor.Price]: 'Price',
  [PresetExtractor.Person]: 'Person',
  [PresetExtractor.Organization]: 'Organization',
  [PresetExtractor.OrganizationEnglish]: 'Organization English',
  [PresetExtractor.Ein]: 'Employer Identification Number',
  [PresetExtractor.Vin]: 'Vehicle Identification Number',
  [PresetExtractor.LicensePlateJpClass]: 'License Plate JP Class',
  [PresetExtractor.LicensePlateJpText]: 'License Plate JP Text',
};

export interface UnguidedPresetExtractionRequest {
  version: '2.0';
  'extraction-type': ExtractionType.Unguided;
  'field-type': ExtractionFieldType;
  'field-name': string;
  'extractor-type': ExtractorType.Preset;
  'preset-extractor': PresetExtractor;
  // deprecated fields
  name?: string;
  methods?: [];
}

export interface UnguidedKeyValuePairExtractionRequest {
  version: '2.0';
  'extraction-type': ExtractionType.Unguided;
  'field-type': ExtractionFieldType;
  'field-name': string;
  'extractor-type': ExtractorType.KeyValuePair;
  'search-keys': string[];
  // deprecated fields
  name?: string;
  methods?: [];
}

export interface UnguidedPaystubExtractionRequest {
  version: '2.0';
  'extraction-type': ExtractionType.Unguided;
  'field-type': ExtractionFieldType;
  'field-name': string;
  'extractor-type': ExtractorType.Paystub;
  // deprecated fields
  name?: string;
  methods?: [];
}

export interface UnguidedBankStatementExtractionRequest {
  version: '2.0';
  'extraction-type': ExtractionType.Unguided;
  'field-type': ExtractionFieldType;
  'field-name': string;
  'extractor-type': ExtractorType.BankStatement;
  // deprecated fields
  name?: string;
  methods?: [];
}

export interface UnguidedTablesExtractionRequest {
  version: '2.0';
  'extraction-type': ExtractionType.Unguided;
  'field-type': ExtractionFieldType;
  'field-name': string;
  'extractor-type': ExtractorType.Tables;
  // deprecated fields
  name?: string;
  methods?: [];
}

export interface UnguidedAiExtractionParamsPropertyValue {
  type: 'string' | 'array';
  items?: { type: 'string' };
  description?: string;
}

// TODO: Clean this up after Docs GA Product Readiness is fully launched
export enum UnguidedAiExtractionFieldType {
  String = 'string',
  Address = 'address',
  Date = 'date',
  NameFull = 'name_full',
  Number = 'number',
  PhoneNumber = 'phone_number',
}

export interface UnguidedAiExtractionParams {
  type: 'object';
  properties: Record<string, UnguidedAiExtractionParamsPropertyValue>;
  title: string;
  description?: string;
}

export interface UnguidedAiExtractionRequest {
  version: '2.0';
  'extraction-type': ExtractionType.Unguided;
  'field-name': string;
  'field-type': ExtractionFieldType.String;
  'extractor-type': ExtractorType.Ai;
  'extraction-params': UnguidedAiExtractionParams;
  'primary-property-key': string;
}

export type UnguidedExtractionExtractors =
  | PresetExtractor
  | RegexExtractor[]
  | TextExtractor[]
  | string[];

export type UnguidedExtractionRequest =
  | UnguidedBankStatementExtractionRequest
  | UnguidedKeyValuePairExtractionRequest
  | UnguidedPresetExtractionRequest
  | UnguidedRegexExtractionRequest
  | UnguidedPaystubExtractionRequest
  | UnguidedTablesExtractionRequest
  | UnguidedTextExtractionRequest
  | UnguidedAiExtractionRequest;

export interface GuidedExtractionRequestBase {
  version: '2.0' | '3.0';
  'extraction-type': ExtractionType.Guided;
  'field-type': ExtractionFieldType;
  'field-name': string;
  'edit-distance-maximum'?: number;
  'match-level-minimum'?: MatchLevel;
  'definition-type'?: DefinitionType;
  preset?: GuidedPreset;
  // deprecated fields
  name?: string;
  methods?: [];
}

export interface GuidedExtractionRequestV2 extends GuidedExtractionRequestBase {
  version: '2.0';
  'ai-model'?: string;
  'edit-distance-maximum': number;
  'match-level-minimum': MatchLevel;
}

export interface GuidedExtractionRequestV3 extends GuidedExtractionRequestBase {
  version: '3.0';
  subfields: Array<SubfieldMatchConfig>;
  'match-criteria': MatchCriteria[];
}

export type GuidedExtractionRequest = GuidedExtractionRequestV2 | GuidedExtractionRequestV3;

interface LogicComposition {
  type: 'logic_composition';
  'match-level-minimum': MatchLevel;
  operator: 'and' | 'or' | 'comparison';
  'comparison-method': ComparisonMethod;
  'normalization-methods': NormalizationMethod[];
  'augmentation-methods': AugmentationMethod[];
}

export interface SubfieldMatchCriteria {
  'match-level': MatchLevel;
  'logic-composition': LogicComposition;
}

interface SearchMethod {
  method: 'full_string' | 'substring' | 'regex';
}

interface SubfieldMatchConfig {
  'source-field': string;
  'search-method': SearchMethod;
  criteria: SubfieldMatchCriteria[];
  'ai-model'?: string;
}

export interface MatchCriteria {
  'match-level': MatchLevel;
  'subfields-composite-comparison-method': CompositeComparisonMethod;
}

type SubfieldMatchFoundCompositeComparisonMethod = CompositeComparisonMethodBase & {
  type: CompositeComparisonMethodType.SubfieldMatchFound;
  'match-level-minimum': MatchLevel;
  subfield: string;
};

type LogicComparisonCompositeComparisonMethod = CompositeComparisonMethodBase & {
  operator: 'and' | 'or' | 'comparison';
  components: LogicComparisonCompositeComparisonMethod[];
  'comparison-method'?: CompositeComparisonMethod;
};

interface CompositeComparisonMethodBase {
  type: CompositeComparisonMethodType;
}

type CompositeComparisonMethod =
  | SubfieldMatchFoundCompositeComparisonMethod
  | LogicComparisonCompositeComparisonMethod;

export type ExtractionRequest = UnguidedExtractionRequest | GuidedExtractionRequest;

export enum CompositeComparisonMethodType {
  SubfieldMatchFound = 'subfield_match_found',
  LogicComposition = 'logic_composition',
}

export interface Word {
  text: string;
  vertices: BoundingBox;
}

export interface Line {
  text: string;
  vertices: BoundingBox;
  words?: Word[];
  'paragraph-id'?: number | null;
}

export interface Face {
  vertices: BoundingBox;
  image: string;
}

export interface Page {
  lines: Line[];
  faces: Face[];
  'page-angle': number;
  'page-number': number;
}

export interface InsightResponseResult {
  value: string;
  truncated: boolean;
}

export interface InsightResponse {
  key: string;
  results: InsightResponseResult[];
}

export enum InsightType {
  String = 'string',
  MultiSelect = 'multi_select',
}

export interface StringInsightRequest {
  type: InsightType.String;
}

export enum InsightResponseType {
  Single = 'single',
  Multiple = 'multiple',
}

export interface MultiSelectInsightOption {
  value: string;
  description: string | null;
}

export interface MultiSelectInsightRequest {
  type: InsightType.MultiSelect;
  'response-type': InsightResponseType;
  options: MultiSelectInsightOption[];
  'default-response': string | null;
}

export type InsightRequest = (StringInsightRequest | MultiSelectInsightRequest) & {
  key: string;
  label: string;
  instruction: string;
  model?: string;
};

export enum FieldWriteBackSourceType {
  Extraction = 'extraction',
  Insight = 'insight',
}

// todo(danielaraujo): support remaining format types
export enum FieldWriteBackFormatType {
  Array = 'array',
  TemplatedString = 'templated_string',
}

export interface FieldWriteBackFormatArray {
  type: FieldWriteBackFormatType.Array;
  reference: string;
}

export interface FieldWriteBackFormatTemplateString {
  type: FieldWriteBackFormatType.TemplatedString;
  template: string;
}

export type FieldWriteBackFormat = FieldWriteBackFormatArray | FieldWriteBackFormatTemplateString;

interface NormalizationMethodsMapping {
  reference: string;
  'normalization-methods': NormalizationMethod[];
}

export interface FieldWriteBackFormatter {
  format: FieldWriteBackFormat;
  'normalization-methods-mappings': NormalizationMethodsMapping[];
}

export enum FieldWriteBackSelectorType {
  SelectAll = 'select_all',
  FirstOccurrence = 'first_occurrence',
  GreatestMatchLevel = 'greatest_match_level',
  LargestOccurrence = 'largest_occurrence',
  LastOccurrence = 'last_occurrence',
  MostFrequent = 'most_frequent',
  SmallestOccurrence = 'smallest_occurrence',
  ClosestToCorner = 'closest_to_corner',
}

interface FieldWriteBackSelector {
  type: FieldWriteBackSelectorType;
  'specified-pages'?: number[];
  'specified-field-identifiers': string[];
}

// todo(danielaraujo): support selector
export interface FieldWriteBackOption {
  'extraction-name': string; // TODO(danielaraujo): remove when extraction-name is migrated to source-key
  'source-key': string;
  'source-type': FieldWriteBackSourceType;
  selector?: FieldWriteBackSelector;
  formatter: FieldWriteBackFormatter;
}

export interface FieldWriteBackRequest {
  'field-name': string;
  options: FieldWriteBackOption[];
}

interface DocumentGenericConfig {
  extractions: ExtractionRequest[];
  insights: InsightRequest[];
}

export const DEFAULT_DOCUMENT_GENERIC_CONFIG: DocumentGenericConfig = {
  extractions: [],
  insights: [],
};

export interface DocumentGenericAttributes extends DocumentCoreAttributes {
  files: DocumentFile[];
  'files-normalized': DocumentFile[];
  pages?: Page[] | null;
  'extraction-responses'?: ExtractionResponse[] | null;
  'insight-responses'?: InsightResponse[] | null;
  'detected-language'?: GoogleSupportedLanguage | null;
  fields: FieldsMap;
  config?: DocumentGenericConfig;
}

export interface DocumentGovernmentIdAttributes extends DocumentCoreAttributes {
  'front-photos'?: DocumentFile[];
  'back-photos'?: DocumentFile[];
  'front-photos-processed'?: DocumentFile[];
  'back-photos-processed'?: DocumentFile[];
  'name-first'?: string;
  'name-middle'?: string;
  'name-last'?: string;
  'name-suffix'?: string;
  birthdate?: string;
  sex?: string;
  'address-street-1'?: string;
  'address-street-2'?: string;
  'address-city'?: string;
  'address-subdivision'?: string;
  'address-postal-code'?: string;
  'issue-date'?: string;
  'expiration-date'?: string;
  'identification-number'?: string;
  nationality?: string;
}

export interface DocumentRelationships {
  inquiry?: ApiRelationship<ApiModel.Inquiry>;
}

export type DocumentGeneric = ApiResource<
  DocumentGenericAttributes,
  DocumentRelationships,
  ApiModel.DocumentGeneric
>;

export type DocumentGovernmentId = ApiResource<
  DocumentGovernmentIdAttributes,
  DocumentRelationships,
  ApiModel.DocumentGovernmentId
>;

export const BankStatementDataField = 'bank-statement-data';

export enum BankStatementTransactionType {
  Debit = 'DEBIT',
  Credit = 'CREDIT',
}

type BankStatementEntry = {
  'financial-transactions': {
    date: string;
    description: string;
    amount: number;
    type: string;
    labels: string[];
    'attachment-id'?: string;
    document?: string | null;
    'account-number'?: string | null;
  }[];
  'computed-data': {
    'calculated-beginning-date': string;
    'calculated-ending-date': string;
    'calculated-ending-balance': number;
    'average-transaction-amount': number;
    'average-deposit-amount': number;
    'minimum-deposit-amount': number;
    'maximum-deposit-amount': number;
    'total-deposit-amount': number;
    'total-deposit-count': number;
    'total-withdrawal-amount': number;
    'total-withdrawal-count': number;
    'total-transaction-count': number;
    'average-daily-balance': number;
    'average-weekday-balance': number;
    'maximum-withdrawal-amount': number;
    'days-with-negative-balance': string[];
    'weekdays-with-negative-balance': string[];
    'total-estimated-revenue': number;
    'non-sufficient-funds-count': number;
    'daily-balances': {
      date: string;
      balance: number;
    }[];
  };
};

export type BankStatementData = {
  'file-info'?: {
    'file-name': string;
    'attachment-id': string;
  };
  'statement-info'?: {
    'account-holder': string;
    'account-number': string;
    'ach-routing-number': string;
    'aba-routing-number': string;
    'account-type': string;
    'account-category': string;
    'bank-name': string;
    'bank-address': string;
    'account-holder-address': string;
    'statement-starting-date': string;
    'statement-ending-date': string;
    'beginning-balance': number;
    'ending-balance': number;
  };
  'monthly-data': ({ year: number; month: number } & BankStatementEntry)[];
  'combined-data': BankStatementEntry;
};

// This will be a union type of all document resources.
export type Document = DocumentGeneric | DocumentGovernmentId;
